import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { AssetChip } from '../AIStudioShared';
import { VoiceSelect } from './VoiceLibrary';
import { CinematographyPanel } from './CinematographyPanel';
import { SCENE_KEYS, SHOT_FILE_KEYS, SceneSetupFields } from './SceneSetupFields';

const sel = 'w-full rounded-md border border-neutral-800 bg-neutral-950 px-2 py-2 text-xs text-neutral-200';
const Label = ({ children }) => <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-500">{children}</div>;

/** Shared shot editor fields — used by the manual Director's Chair and the Edit Shot dialog. */
export function ShotFields({ project, catalog, voices, value, onChange, testPrefix }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const kit = project.show_kit || [];
  const characters = kit.filter((a) => a.kind === 'character');
  const eps = project.format === 'series' ? project.episodes_count || 1 : 1;
  const toggle = (id) => set({ anchor_asset_ids: value.anchor_asset_ids.includes(id)
    ? value.anchor_asset_ids.filter((x) => x !== id) : [...value.anchor_asset_ids, id] });
  const story = (project.story?.scenes || []).filter((x) => x.episode === value.episode);
  const speakerLang = characters.find((c) => c.name === value.speaker)?.language;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-[1fr_120px_120px]">
        <div><Label>Shot title</Label>
          <Input value={value.title} onChange={(e) => set({ title: e.target.value })} placeholder="e.g. The rooftop confession"
            className="border-neutral-800 bg-neutral-950 text-sm" data-testid={`${testPrefix}-title`} /></div>
        <div><Label>Episode</Label>
          <select value={value.episode} onChange={(e) => set({ episode: Number(e.target.value) })} className={sel} data-testid={`${testPrefix}-episode`} disabled={eps === 1}>
            {Array.from({ length: eps }, (_, i) => <option key={i + 1} value={i + 1}>EP {i + 1}</option>)}
          </select></div>
        <div><Label>Length</Label>
          <select value={value.duration_seconds} onChange={(e) => set({ duration_seconds: Number(e.target.value) })} className={sel} data-testid={`${testPrefix}-duration`}>
            {[2, 3, 4, 5, 6, 7, 8].map((d) => <option key={d} value={d}>{`${d}s`}</option>)}
          </select></div>
      </div>
      <div><Label>What happens (visual action)</Label>
        <Textarea rows={2} value={value.description} onChange={(e) => set({ description: e.target.value })}
          placeholder="Rain lashes the neon street as Ligaya runs toward the last jeepney, clutching the ledger."
          className="border-neutral-800 bg-neutral-950 text-sm" data-testid={`${testPrefix}-description`} /></div>
      <div className="grid gap-3 md:grid-cols-[180px_1fr_1fr_180px]">
        <div><Label>Speaker</Label>
          <select value={value.speaker} onChange={(e) => set({ speaker: e.target.value })} className={sel} data-testid={`${testPrefix}-speaker`}>
            <option value="">No dialogue / narrator</option>
            {characters.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select></div>
        <div><Label>Dialogue ({speakerLang || project.language || 'English'})</Label>
          <Input value={value.dialogue} onChange={(e) => set({ dialogue: e.target.value })} placeholder="Spoken line"
            className="border-neutral-800 bg-neutral-950 text-sm" data-testid={`${testPrefix}-dialogue`} /></div>
        <div><Label>Narration / voice-over</Label>
          <Input value={value.narration} onChange={(e) => set({ narration: e.target.value })} placeholder="Optional narrator line"
            className="border-neutral-800 bg-neutral-950 text-sm" data-testid={`${testPrefix}-narration`} /></div>
        <div><Label>Voice override</Label>
          <VoiceSelect voices={voices} value={value.voice_id} onChange={(v) => set({ voice_id: v })} testId={`${testPrefix}-voice`} placeholder="Character / narrator voice" /></div>
      </div>
      {kit.length > 0 && (
        <div><Label>Lock cast & location in frame</Label>
          <div className="flex flex-wrap gap-2">
            {kit.map((a) => <AssetChip key={a.id} asset={a} selected={value.anchor_asset_ids.includes(a.id)} onToggle={() => toggle(a.id)}
              data-testid={`${testPrefix}-anchor-${a.id}`} />)}
          </div></div>
      )}
      <div className="space-y-3 rounded-lg border border-neutral-800 bg-neutral-950/50 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs font-semibold text-neutral-200">Scene setup <span className="font-normal text-neutral-400">· type, time, weather, mood, blocking, wardrobe, props & sound — all optional</span></div>
          {story.length > 0 && (
            <select value={value.story_scene_id || ''} onChange={(e) => set({ story_scene_id: e.target.value })} className={`${sel} w-auto max-w-[260px]`} data-testid={`${testPrefix}-story-scene`}>
              <option value="">Not linked to a story scene</option>
              {story.map((x, i) => <option key={x.id} value={x.id}>{`Scene ${i + 1} · ${x.heading}`}</option>)}
            </select>
          )}
        </div>
        <SceneSetupFields catalog={catalog} value={value} onChange={set} testPrefix={testPrefix} withFiles />
      </div>
      <div className="rounded-lg border border-neutral-800 bg-neutral-950/50 p-4">
        <CinematographyPanel catalog={catalog} value={value.cinematography} onChange={(c) => set({ cinematography: c })} testPrefix={`${testPrefix}-cine`} />
      </div>
    </div>
  );
}

const SETUP_KEYS = [...SCENE_KEYS, ...SHOT_FILE_KEYS, 'story_scene_id'];
export const sceneSetup = (s) => Object.fromEntries(SETUP_KEYS.map((k) => [k, s?.[k] || '']));

export const emptyShot = (episode = 1, carry = {}) => ({
  title: '', description: '', episode, duration_seconds: 3, speaker: '', dialogue: '', narration: '',
  voice_id: '', anchor_asset_ids: [], cinematography: {}, ...sceneSetup(carry),
});

export const shotFromScene = (s) => ({
  title: s.title || '', description: s.description || '', episode: s.episode || 1, duration_seconds: Math.min(8, Math.max(2, s.duration_seconds || 4)),
  speaker: s.speaker || '', dialogue: s.dialogue || '', narration: s.narration || '', voice_id: s.voice_id || '',
  anchor_asset_ids: s.anchor_asset_ids || [], cinematography: s.cinematography || {}, ...sceneSetup(s),
});
